import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import NavBar from '../../components/layout/NavBar';
import SiteFooter from '../../components/layout/SiteFooter';
import SEO from '../../components/SEO';
import Breadcrumbs from '../../components/Breadcrumbs';
import { webPageSchema, breadcrumbSchema } from '../../schema';
import { POSTS } from './posts/meta';

const BlogIndex = () => {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle');

  const handleSubscribe = async (e) => {
    e.preventDefault();
    setStatus('submitting');
    try {
      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({
          access_key: '36bd61b8-e929-4fd7-ae6f-049dd98ecde2',
          subject: 'New Newsletter Signup from SaniQ Blog',
          from_name: 'SaniQ Blog',
          name: email.split('@')[0] || 'Subscriber',
          email,
          replyto: email,
          message: `New newsletter signup: ${email}`,
          'Email Address': email,
        }),
      });
      const result = await response.json();
      if (!response.ok || !result.success) throw new Error(result.message || 'Submission failed');
      setStatus('success');
    } catch (err) {
      console.error('Blog newsletter submission failed', err);
      setStatus('error');
    }
  };

  return (
    <div className="relative w-full min-h-screen">
      <SEO
        title="Blog"
        description="Hygiene science, product engineering notes, and what it takes to get the world's first intelligent doormat to your door."
        path="/blog"
        schema={[
          webPageSchema({ title: 'The SaniQ Blog', description: 'SaniQ blog index.', path: '/blog' }),
          breadcrumbSchema([{ name: 'Home', path: '/' }, { name: 'Blog', path: '/blog' }]),
        ]}
      />
      <NavBar />
      <Breadcrumbs trail={[{ name: 'Home', path: '/' }, { name: 'Blog', path: '/blog' }]} />

      <section className="px-8 pb-12 pt-8 text-center max-w-3xl mx-auto">
        <p className="text-cyan-400 font-mono text-[10px] tracking-[0.4em] uppercase mb-4">Journal</p>
        <h1 className="text-4xl md:text-6xl font-extralight tracking-tighter text-white mb-6">The SaniQ Blog</h1>
        <p className="text-white/45 text-sm md:text-base leading-relaxed max-w-xl mx-auto">
          Hygiene science, product engineering notes, and what it takes to get the world&apos;s first intelligent doormat to your door.
        </p>
      </section>

      <section className="pb-14 px-8">
        <h2 className="max-w-5xl mx-auto text-lg font-medium text-white mb-6">Most Popular</h2>
        <div className="max-w-5xl mx-auto flex gap-4 overflow-x-auto pb-2">
          {POSTS.map((p) => (
            <Link
              key={p.slug}
              to={`/blog/${p.slug}`}
              className="glass min-w-[220px] rounded-2xl p-5 shrink-0 border border-white/10 hover:border-cyan-500/30 transition-colors"
            >
              <p className="text-cyan-400 text-[10px] uppercase tracking-widest font-bold mb-2">{p.category}</p>
              <p className="text-sm font-medium text-white leading-snug">{p.title}</p>
            </Link>
          ))}
        </div>
      </section>

      <section className="pb-16 px-8">
        <h2 className="max-w-4xl mx-auto text-lg font-medium text-white mb-6">Latest Articles</h2>
        <div className="max-w-4xl mx-auto flex flex-col gap-4">
          {POSTS.map((post) => (
            <Link
              key={post.slug}
              to={`/blog/${post.slug}`}
              className="glass rounded-2xl p-4 md:p-5 grid grid-cols-1 md:grid-cols-[150px_1fr] gap-5 items-center border border-white/10 hover:border-cyan-500/30 transition-colors"
            >
              <div className="h-24 md:h-full rounded-xl bg-white/[0.03] border border-white/10" />
              <div>
                <p className="text-cyan-400 text-[10px] uppercase tracking-widest font-bold">{post.category}</p>
                <h3 className="text-white text-base md:text-lg font-medium mt-2 mb-1">{post.title}</h3>
                <p className="text-white/30 text-[11px] mb-2">SaniQ Team · {post.date} · {post.readTime}</p>
                <p className="text-white/45 text-sm leading-relaxed">{post.excerpt}</p>
                <span className="inline-block mt-2 text-cyan-400 text-xs font-bold">Read more →</span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      <section className="pb-20 px-8">
        <div className="max-w-2xl mx-auto rounded-3xl border border-cyan-500/30 bg-cyan-500/[0.06] p-9 text-center">
          <h2 className="text-white text-xl font-medium mb-2">Get hygiene insights + launch updates</h2>
          {status === 'success' ? (
            <p className="text-cyan-400 text-sm">You're subscribed. Thanks for reading.</p>
          ) : (
            <>
              <p className="text-white/45 text-sm mb-6">One email, roughly monthly. No spam.</p>
              <form onSubmit={handleSubscribe} className="flex gap-3 max-w-sm mx-auto">
                <input
                  required
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={status === 'submitting'}
                  placeholder="you@email.com"
                  className="flex-1 rounded-full border border-white/10 bg-black/40 px-4 py-3 text-xs text-white focus:border-cyan-500 focus:outline-none disabled:opacity-50"
                />
                <button
                  type="submit"
                  disabled={status === 'submitting'}
                  className="px-6 py-3 bg-cyan-500 text-black text-[11px] font-bold uppercase tracking-widest rounded-full hover:bg-cyan-400 transition-colors disabled:opacity-50 whitespace-nowrap"
                >
                  Subscribe
                </button>
              </form>
              {status === 'error' && <p className="text-red-400 text-[11px] mt-3">Something went wrong. Please try again.</p>}
            </>
          )}
        </div>
      </section>

      <SiteFooter />
    </div>
  );
};

export default BlogIndex;
